import { observer } from 'mobx-react'
import React, { ReactElement, useState } from 'react'
import { PrimaryButton } from './Button'
import Dropdown, { DropdownItem } from './Dropdown'
import { InputLabel, TextInputWithLabel } from './Input'
import Modal, { ModalFooter, ModalProps } from './Modal'
import Radom from '../state/Radom'
import User from '../state/User'
import { errorToast, successToast } from '../util/Util'
import { FIAT_ACCOUNT_FLAGS, FIAT_CURRENCIES_FOR_ACCOUNT_TYPE, FIAT_CURRIENCES_FOR_NETWORK, FIAT_DESTINATION_CURRENCY_IMG, FIAT_PAYMENT_RAIL_IMG, FiatAccountType, FiatDestinationCurrency, FiatDestinationNetwork, RADOM_COLORS } from '../util/Constants'

interface CreateVirtualAccountModalProps extends ModalProps {
  onAccountCreated: () => void
  availableAccountTypes: Array<{ type: FiatAccountType, available: boolean }>
  availableCurrencies: FiatDestinationCurrency[]
}

const CreateVirtualAccountModal = observer(({ ...props }: CreateVirtualAccountModalProps): ReactElement => {
  const [accountName, setAccountName] = useState('')
  const [fiatAccountType, setFiatAccountType] = useState<FiatAccountType>()
  const [destinationNetwork, setDestinationNetwork] = useState<FiatDestinationNetwork>()
  const [destinationCurrency, setDestinationCurrency] = useState<FiatDestinationCurrency>()
  const [currencies, setCurrencies] = useState<FiatDestinationCurrency[]>(props.availableCurrencies)
  const [networks, setNetworks] = useState<FiatDestinationNetwork[]>(
    Object.values(FiatDestinationNetwork).filter(item => {
      const availableCurrencies = props.availableCurrencies
      const allCurrencies = FIAT_CURRIENCES_FOR_NETWORK[item]
      return allCurrencies.every(curr => availableCurrencies.includes(curr))
    }))
  const [isLoading, setIsLoading] = useState(false)

  const createFiatAccount = async (): Promise<void> => {
    if (!fiatAccountType || !destinationNetwork) {
      errorToast('')
      return
    }

    setIsLoading(true)
    try {
      await Radom.createDepositAccount({
        name: accountName,
        accountDetails: {
          Fiat: {
            currency: fiatAccountType,
            network: destinationNetwork,
            token: destinationCurrency
          }
        }
      })
      props.onAccountCreated()
      successToast('Virtual account created')
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  return <Modal {...props} innerStyles={{ width: 620 }}>
    <div style={{
      display: 'flex',
      flexGrow: 1,
      flexDirection: 'column',
      justifyContent: 'start',
      gap: 16
    }}>
      <div style={{
        padding: '14px 16px',
        borderRadius: 16,
        border: '1px solid #eceff3',
        background: '#f9fafb',
        color: '#5f6675',
        fontSize: 13.5,
        lineHeight: 1.55
      }}>
        Create a virtual account for incoming bank transfers and choose the payout rail your customers should use.
      </div>

      <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', rowGap: 16 }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
          padding: 18,
          borderRadius: 18,
          border: '1px solid #eceff3',
          background: '#fcfcfd'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ color: '#20242d', fontSize: 15, fontWeight: 500 }}>Account details</div>
            <div style={{ color: '#6b7280', fontSize: 13, lineHeight: 1.5 }}>
              Name the account and select the bank-account format you want to issue.
            </div>
          </div>

          <TextInputWithLabel
            label='Account nickname'
            placeholder='Enter a nickname for this account'
            disabled={isLoading}
            required
            value={accountName}
            onChange={e => {
              const el = (e.target as HTMLInputElement)
              const value = el.value
              setAccountName(value)
            }}
          />

          <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10 }}>
            <InputLabel>Account type</InputLabel>
            <Dropdown
              key={fiatAccountType}
              disabled={isLoading}
              outerStyle={{ minHeight: 46 }}
              selectedContent={
                fiatAccountType
                  ? <div style={{
                    fontSize: 14,
                    alignItems: 'center',
                    display: 'flex',
                    gap: 10
                  }}>
                    <img
                      style={{
                        width: 20,
                        maxHeight: 20,
                        maxWidth: 20
                      }}
                      src={`https://pay.radom.com/flags/${FIAT_ACCOUNT_FLAGS[fiatAccountType]}.svg`}
                    />
                    {fiatAccountType}
                  </div>
                  : <span style={{ fontSize: 14, color: '#747894' }}>Select bank account type</span>
              }
              dropdownContent={
                props.availableAccountTypes.map(c =>
                  <DropdownItem
                    key={c.type}
                    onClick={() => {
                      setFiatAccountType(c.type)
                      setDestinationNetwork(undefined)
                      setDestinationCurrency(undefined)
                      const finalCurrencies = props.availableCurrencies.filter(item =>
                        FIAT_CURRENCIES_FOR_ACCOUNT_TYPE[c.type].includes(item))
                      setCurrencies(finalCurrencies)
                      setNetworks(Object.values(FiatDestinationNetwork).filter(item => {
                        const allCurrencies = FIAT_CURRIENCES_FOR_NETWORK[item]
                        return allCurrencies.every(curr => finalCurrencies.includes(curr))
                      }))
                    }}
                    style={{
                      fontSize: 14,
                      alignItems: 'center',
                      display: 'flex',
                      gap: 10,
                      ...(!c.available
                        ? {
                          cursor: 'not-allowed',
                          pointerEvents: 'none',
                          backgroundColor: RADOM_COLORS.GRAY_LIGHTEST,
                          opacity: 0.5
                        }
                        : {})
                    }}>
                    <img
                      style={{
                        width: 20,
                        maxHeight: 20,
                        maxWidth: 20
                      }}
                      src={`https://pay.radom.com/flags/${FIAT_ACCOUNT_FLAGS[c.type]}.svg`}
                    />
                    {c.type}
                  </DropdownItem>
                )
              } />
          </div>
        </div>

        {
          fiatAccountType && <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
            padding: 18,
            borderRadius: 18,
            border: '1px solid #eceff3',
            background: '#fcfcfd'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ color: '#20242d', fontSize: 15, fontWeight: 500 }}>Settlement routing</div>
              <div style={{ color: '#6b7280', fontSize: 13, lineHeight: 1.5 }}>
                Choose the destination currency and payment rail available for this account.
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
              <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10, minWidth: 0 }}>
                <InputLabel>Destination currency</InputLabel>
                <Dropdown
                  key={destinationCurrency}
                  disabled={isLoading || currencies.length === 0}
                  outerStyle={{ minHeight: 46 }}
                  selectedContent={
                    destinationCurrency
                      ? <div style={{
                        fontSize: 14,
                        alignItems: 'center',
                        display: 'flex',
                        gap: 10
                      }}>
                        <img
                          style={{
                            width: 20,
                            maxHeight: 20,
                            maxWidth: 20
                          }}
                          src={FIAT_DESTINATION_CURRENCY_IMG[destinationCurrency]}
                        />
                        {destinationCurrency}
                      </div>
                      : <span style={{ fontSize: 14, color: '#747894' }}>Select destination currency</span>
                  }
                  dropdownContent={
                    currencies.map(d =>
                      <DropdownItem
                        key={d}
                        onClick={() => {
                          setDestinationCurrency(d)
                          setNetworks(Object.values(FiatDestinationNetwork).filter(item => {
                            const allCurrencies = FIAT_CURRIENCES_FOR_NETWORK[item]
                            return allCurrencies.includes(d)
                          }))
                        }}
                        style={{
                          fontSize: 14,
                          alignItems: 'center',
                          display: 'flex',
                          gap: 10
                        }}>
                        <img
                          style={{
                            width: 20,
                            maxHeight: 20,
                            maxWidth: 20
                          }}
                          src={FIAT_DESTINATION_CURRENCY_IMG[d]}
                        />
                        {d}
                      </DropdownItem>
                    )
                  } />
              </div>

              <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 10, minWidth: 0 }}>
                <InputLabel>Destination network</InputLabel>
                <Dropdown
                  key={destinationNetwork}
                  disabled={isLoading || networks.length === 0}
                  outerStyle={{ minHeight: 46 }}
                  selectedContent={
                    destinationNetwork
                      ? <div style={{
                        fontSize: 14,
                        alignItems: 'center',
                        display: 'flex',
                        gap: 10
                      }}>
                        <img
                          style={{
                            width: 20,
                            maxHeight: 20,
                            maxWidth: 20
                          }}
                          src={FIAT_PAYMENT_RAIL_IMG[destinationNetwork]}
                        />
                        {destinationNetwork}
                      </div>
                      : <span style={{ fontSize: 14, color: '#747894' }}>Select destination network</span>
                  }
                  dropdownContent={
                    networks.map(p =>
                      <DropdownItem
                        key={p}
                        onClick={() => {
                          setDestinationNetwork(p)
                          setCurrencies(FIAT_CURRIENCES_FOR_NETWORK[p].filter(item =>
                            props.availableCurrencies.includes(item)))
                        }}
                        style={{
                          fontSize: 14,
                          alignItems: 'center',
                          display: 'flex',
                          gap: 10
                        }}>
                        <img
                          style={{
                            width: 20,
                            maxHeight: 20,
                            maxWidth: 20
                          }}
                          src={FIAT_PAYMENT_RAIL_IMG[p]}
                        />
                        {p}
                      </DropdownItem>
                    )
                  } />
              </div>
            </div>
          </div>
        }
      </div>
    </div>

    {
      User.organization?.verificationStatus === 'Verified' &&
      <ModalFooter $fullWidthAction>
        <PrimaryButton
          disabled={!accountName || !fiatAccountType || !destinationNetwork }
          isLoading={isLoading}
          style={{ width: '100%' }}
          onClick={() => {
            createFiatAccount()
          }}>
          Create virtual account
        </PrimaryButton>
      </ModalFooter>
    }
  </Modal>
})

export default CreateVirtualAccountModal
